import { ArrowUpward, Attachment, CloudUpload, ContentCopy, CreateNewFolder, Delete, DriveFileMove, Link as LinkIcon, Refresh } from '@mui/icons-material';
import { Box, Breadcrumbs, Button, Chip, IconButton, LinearProgress, List, ListItem, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Typography } from '@mui/material';
import React from 'react';
import { api } from '../api/client';
import Layout from '../components/Layout';

const CloudPage = () => {
  const [files, setFiles] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [prefix, setPrefix] = React.useState('');
  const [uploading, setUploading] = React.useState(false);
  const [tree, setTree] = React.useState({ folders: [], files: [] });
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [contextKey, setContextKey] = React.useState(null);

  const loadFiles = async () => {
    setLoading(true);
    try {
      const res = await api.get('/storage/files', { params: { prefix } });
      const payload = res.data?.data || res.data || {};
      setTree({
        folders: Array.isArray(payload.folders) ? payload.folders : [],
        files: Array.isArray(payload.files) ? payload.files : []
      });
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { loadFiles(); }, [prefix]);

  const onSelectFiles = async (e) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    try {
      for (const f of fileList) {
        const form = new FormData();
        form.append('file', f);
        if (prefix) form.append('prefix', prefix.endsWith('/') ? prefix : prefix + '/');
        await api.post('/storage/files', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      await loadFiles();
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const onDelete = async (key) => {
    await api.delete(`/storage/files/${encodeURIComponent(key)}`);
    await loadFiles();
  };

  const onDeleteFolder = async (key) => {
    await api.delete('/storage/folders', { params: { prefix: key } });
    await loadFiles();
  };

  const onGetLink = async (key) => {
    const res = await api.get(`/storage/files/${encodeURIComponent(key)}/url`, { params: { expires: 3600 } });
    const url = res.data?.url;
    if (url) window.open(url, '_blank');
  };

  const enterFolder = (folderKey) => {
    setPrefix(folderKey);
  };

  const goUp = () => {
    if (!prefix) return;
    const parts = prefix.replace(/\/$/, '').split('/');
    parts.pop();
    const up = parts.length ? parts.join('/') + '/' : '';
    setPrefix(up);
  };

  const createFolder = async () => {
    const name = prompt('Название папки');
    if (!name) return;
    await api.post('/storage/folders', { prefix, name });
    await loadFiles();
  };

  const openMenu = (event, key) => {
    setContextKey(key);
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => { setAnchorEl(null); setContextKey(null); };

  const moveFile = async () => {
    const dst = prompt('Переместить в (новый ключ):', contextKey);
    if (!dst) return closeMenu();
    await api.post('/storage/files/move', { src: contextKey, dst });
    closeMenu();
    await loadFiles();
  };

  const copyLink = async () => {
    const res = await api.get(`/storage/files/${encodeURIComponent(contextKey)}/url`, { params: { expires: 3600 } });
    const url = res.data?.url;
    if (url) await navigator.clipboard.writeText(url);
    closeMenu();
  };

  const crumbs = (() => {
    const parts = prefix.split('/').filter(Boolean);
    const acc = [];
    let path = '';
    acc.push({ name: 'root', key: '' });
    parts.forEach((p) => { path += p + '/'; acc.push({ name: p, key: path }); });
    return acc;
  })();

  return (
    <Layout>
      <Box sx={{ p: 3, color: 'white' }}>
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold', color: 'rgba(255,255,255,0.95)' }}>Облако</Typography>
        <Breadcrumbs sx={{ mb: 2, color: 'rgba(255,255,255,0.9)' }}>
          {crumbs.map((c) => (
            <Chip
              key={c.key}
              label={c.name}
              onClick={() => enterFolder(c.key)}
              variant="outlined"
              sx={{
                cursor: 'pointer',
                borderColor: 'rgba(255,255,255,0.25)',
                background: 'rgba(255,255,255,0.08)',
                '& .MuiChip-label': { color: 'rgba(255,255,255,0.95)' }
              }}
            />
          ))}
        </Breadcrumbs>
        <Paper sx={{
          p: 2, mb: 2, display: 'flex', gap: 1, alignItems: 'center',
          background: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.15)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2
        }}>
          <IconButton onClick={goUp} title="Вверх" sx={{ color: 'rgba(255,255,255,0.9)' }}><ArrowUpward /></IconButton>
          <IconButton onClick={loadFiles} title="Обновить" sx={{ color: 'rgba(255,255,255,0.9)' }}><Refresh /></IconButton>
          <Button variant="outlined" startIcon={<CreateNewFolder />} onClick={createFolder}
            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.35)', '&:hover': { borderColor: 'rgba(255,255,255,0.6)' } }}
          >Папка</Button>
          <Button variant="contained" startIcon={<CloudUpload />} component="label" sx={{ color: 'white' }}>
            Загрузить
            <input hidden multiple type="file" onChange={onSelectFiles} />
          </Button>
          {uploading && <LinearProgress sx={{ flex: 1 }} />}
        </Paper>

        <Paper sx={{
          p: 0,
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.12)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2
        }}>
          {loading ? (
            <Box sx={{ p: 2 }}><LinearProgress /></Box>
          ) : (
            <List>
              {tree.folders.map((fd) => (
                <ListItem
                  key={fd.key}
                  button
                  onClick={() => enterFolder(fd.key)}
                  onContextMenu={(e) => { e.preventDefault(); openMenu(e, fd.key); }}
                  secondaryAction={
                    <Box>
                      <IconButton onClick={() => onDeleteFolder(fd.key)} title="Удалить папку" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                        <Delete />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemIcon sx={{ color: 'rgba(255,255,255,0.9)' }}><Attachment /></ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{ sx: { color: 'rgba(255,255,255,0.95)' } }}
                    secondaryTypographyProps={{ sx: { color: 'rgba(255,255,255,0.7)' } }}
                    primary={fd.name}
                    secondary="Папка"
                  />
                </ListItem>
              ))}
              {tree.files.map((f) => (
                <ListItem
                  key={f.key}
                  secondaryAction={
                    <Box>
                      <IconButton onClick={() => onGetLink(f.key)} title="Ссылка" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                        <LinkIcon />
                      </IconButton>
                      <IconButton onClick={() => onDelete(f.key)} title="Удалить" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                        <Delete />
                      </IconButton>
                      <IconButton onClick={(e) => openMenu(e, f.key)} title="Ещё" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                        <DriveFileMove />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemIcon sx={{ color: 'rgba(255,255,255,0.9)' }}><Attachment /></ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{ sx: { color: 'rgba(255,255,255,0.95)' } }}
                    secondaryTypographyProps={{ sx: { color: 'rgba(255,255,255,0.7)' } }}
                    primary={f.key || ''}
                    secondary={`Размер: ${f.size || 0} • ${f.last_modified || ''}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Paper>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}
          PaperProps={{ sx: { background: 'rgba(30,30,30,0.95)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' } }}
        >
          <MenuItem onClick={moveFile} sx={{ color: 'white' }}><DriveFileMove fontSize="small" style={{ marginRight: 8 }} /> Переместить</MenuItem>
          <MenuItem onClick={copyLink} sx={{ color: 'white' }}><ContentCopy fontSize="small" style={{ marginRight: 8 }} /> Скопировать ссылку</MenuItem>
        </Menu>
      </Box>
    </Layout>
  );
};

export default CloudPage;

